import type { KeyboardEvent } from "react";

export const EDITORS =
	'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]), .MuiSelect-select:not([aria-disabled])';

const CARET_TYPES = new Set(["text", "search", "tel", "url"]);

export const isAtEdge = (element: HTMLElement, step: -1 | 1) => {
	if (!(element instanceof HTMLInputElement))
		return element.classList.contains("MuiSelect-select");

	if (!CARET_TYPES.has(element.type)) return true;

	const { selectionStart, selectionEnd, value } = element;

	if (selectionStart === null || selectionEnd === null) return false;

	if (element.dataset.sections === undefined && selectionStart !== selectionEnd)
		return false;

	return step < 0 ? selectionStart === 0 : selectionEnd === value.length;
};

export const navigationStep = (event: KeyboardEvent<HTMLElement>) => {
	const isTab = event.key === "Tab";
	const back = isTab ? event.shiftKey : event.key === "ArrowLeft";

	if (!isTab && !back && event.key !== "ArrowRight") return null;

	const step: -1 | 1 = back ? -1 : 1;

	return isTab || isAtEdge(event.target as HTMLElement, step) ? step : null;
};

export const focusEditor = (editor: HTMLElement | null | undefined) => {
	if (!editor) return false;

	editor.focus();
	if (document.activeElement !== editor) return false;
	if (editor instanceof HTMLInputElement) editor.select();

	return true;
};

export const stepEditor = (
	root: HTMLElement | null,
	from: HTMLElement,
	step: -1 | 1,
) => {
	const editors = Array.from(
		root?.querySelectorAll<HTMLElement>(EDITORS) ?? [],
	);
	const current = editors.indexOf(from);

	if (current === -1) return false;

	for (
		let index = current + step;
		index >= 0 && index < editors.length;
		index += step
	)
		if (focusEditor(editors[index])) return true;

	return false;
};

export const verticalStep = (event: KeyboardEvent<HTMLElement>) => {
	if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
		return null;

	const target = event.target as HTMLElement;
	if (!target.matches(EDITORS) || target.dataset.sections !== undefined)
		return null;

	return event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : null;
};

const centerOf = (element: HTMLElement) => {
	const { left, right } = element.getBoundingClientRect();
	return (left + right) / 2;
};

export const stepEditorVertically = (
	root: HTMLElement | null,
	from: HTMLElement,
	step: -1 | 1,
) => {
	const cell = from.closest("td");
	const row = cell?.parentElement;
	if (!root || !cell || !(row instanceof HTMLTableRowElement)) return false;

	const rows = Array.from(root.querySelectorAll("tr"));
	const center = centerOf(cell);

	for (
		let index = rows.indexOf(row) + step;
		index >= 0 && index < rows.length;
		index += step
	) {
		const below = Array.from(rows[index].cells).find((candidate) => {
			const { left, right } = candidate.getBoundingClientRect();
			return left <= center && center < right;
		});
		if (focusEditor(below?.querySelector<HTMLElement>(EDITORS))) return true;
	}

	return false;
};

export type RowStop = { editor: HTMLElement; row: number };

const editorsByRow = (root: HTMLElement): RowStop[] => {
	const rows = Array.from(root.querySelectorAll("tr"));
	const editors = Array.from(root.querySelectorAll<HTMLElement>(EDITORS));

	return rows.flatMap((row, index) => {
		const { top, bottom } = row.getBoundingClientRect();
		const middle = (top + bottom) / 2;

		return editors
			.map((editor) => ({ editor, cell: editor.closest("td") }))
			.filter(({ cell }) => {
				if (!cell) return false;
				const rect = cell.getBoundingClientRect();
				return rect.top <= middle && middle < rect.bottom;
			})
			.sort(
				(a, b) =>
					centerOf(a.cell as HTMLElement) - centerOf(b.cell as HTMLElement),
			)
			.map(({ editor }) => ({ editor, row: index }));
	});
};

export const stepEditorByRows = (
	root: HTMLElement | null,
	from: HTMLElement,
	step: -1 | 1,
	lastStop: RowStop | null,
): RowStop | null => {
	if (!root) return null;

	const stops = editorsByRow(root);
	const ownRow = from.closest("tr");
	const fromRow =
		lastStop?.editor === from
			? lastStop.row
			: ownRow instanceof HTMLTableRowElement
				? ownRow.sectionRowIndex
				: -1;

	let current = stops.findIndex(
		(stop) => stop.editor === from && stop.row === fromRow,
	);
	if (current === -1) current = stops.findIndex((stop) => stop.editor === from);
	if (current === -1) return null;

	for (
		let index = current + step;
		index >= 0 && index < stops.length;
		index += step
	)
		if (focusEditor(stops[index].editor)) return stops[index];

	return null;
};
